// import React, { useEffect, useRef, useState } from "react";
import React from "react";

import { Card, Col, Divider, Input, Popconfirm, Radio, Row, Select, Space } from "antd";

import { Cancel, CheckBoxOutlineBlank, CheckBoxOutlined } from "@mui/icons-material";

import { IQuestion } from "@Totum/QuestionnaireBuilder/Domain/types";
import useDesignerRowPresenter from "@Totum/QuestionnaireBuilder/Presentation//hooks/useDesignerRowPresenter";
import InfoTooltip from "@Totum/Main/Components/shared/InfoTooltip";
import Collapsible from "@Totum/Main/Components/shared/Collapsible";
import TokenisedInput from "@Totum/Main/Components/shared/TokenisedInput";

interface IProps {
    /**
     * The index of the question in the list of questions.
     */
    index: number;

    /**
     * The ID of the question.
     */
    questionId: IQuestion["questionId"];
}

const { Option } = Select;

/**
 * A component that displays a single row in the questionnaire form.
 */
const DesignerRow = ({ index, questionId }: IProps) => {
    const { handlers, uiModel } = useDesignerRowPresenter(questionId);

    const { question } = uiModel;

    return (
        <Collapsible isOpen={!uiModel.isDeleting}>
            <Card className={`newUI-raised fade-collapse ${uiModel.isDeleting ? "effect-active" : ""}`}>
                {/* Header row with column labels */}
                <Row className="designer-row tight">
                    <Col sm={8}>
                        <div className="designer-heading">{`Question ${index + 1}`}</div>
                    </Col>
                    <Col sm={3}>
                        <div className="designer-heading">Type</div>
                    </Col>
                    <Col sm={2}>
                        <div className="designer-heading">
                            <InfoTooltip text="Scored" title={uiModel.tooltipTitle} />
                        </div>
                    </Col>
                    <Col sm={10}>
                        <div className="designer-heading" style={{ display: "flex", flexDirection: "column" }}>
                            Choices
                            {!question?.choices.length ? (
                                <span style={{ color: "red", fontSize: "12px" }}>
                                    Please add at least one option for question to appear
                                </span>
                            ) : null}
                        </div>
                    </Col>
                    <Col sm={1}>
                        <div className="designer-heading">
                            {/* Delete question button with confirmation */}
                            <Popconfirm
                                title="Are you sure you want to delete this item?"
                                onConfirm={handlers.handleDeleteRowClick}
                                okText="Delete"
                                cancelText="Cancel"
                                cancelButtonProps={{ className: "secondary" }}
                            >
                                <div
                                    style={{ pointerEvents: "auto" }}
                                    className="designer-control designer-remove-control"
                                >
                                    <Cancel style={{ cursor: "pointer" }} />
                                </div>
                            </Popconfirm>
                        </div>
                    </Col>
                </Row>

                {/* Main content row with question details */}
                <Row className="designer-row tight">
                    {/* Question title input */}
                    <Col sm={8}>
                        <div className="designer-cell">
                            <Input.TextArea
                                allowClear={false}
                                autoSize={{ minRows: 2 }}
                                className="designer-control"
                                defaultValue={question?.title}
                                key={question?.questionId}
                                style={{ minWidth: "100%" }}
                                onChange={(e) => handlers.handleQuestionTitleChange(e.currentTarget.value)}
                            />
                        </div>
                    </Col>
                    {/* Question type selector */}
                    <Col sm={3}>
                        <div className="designer-cell">
                            <Select
                                className="designer-control"
                                onChange={handlers.handleQuestionTypeChange}
                                style={{ minWidth: "100%" }}
                                value={question?.questionType}
                            >
                                <Option value="boolean">Yes / No</Option>
                                <Option value="choice">MCQ</Option>
                            </Select>
                        </div>
                    </Col>
                    {/* Scored toggle */}
                    <Col sm={2}>
                        <div className="designer-cell designer-control">
                            <button
                                style={{ border: 0, background: "none", cursor: "pointer", fontSize: "2rem" }}
                                onClick={() => handlers.handleQuestionIsScoredChange(!question?.isScored)}
                            >
                                {question?.isScored ? <CheckBoxOutlined /> : <CheckBoxOutlineBlank />}
                            </button>
                        </div>
                    </Col>
                    {/* Choices input (conditional based on question type) */}
                    {question?.questionType && (
                        <Col sm={10}>
                            <div className="designer-cell designer-control">
                                <div className="vertical">
                                    <TokenisedInput
                                        initialOptions={question?.choices.map((x) => x.choiceText)}
                                        handleOnChange={(tags) => handlers.handleChoicesChange(tags)}
                                        handleSelect={
                                            question?.isScored ? handlers.handleChoiceIsCorrectChange : undefined
                                        }
                                        maxOptions={10}
                                        maxTagLength={75}
                                        selectedTag={question?.choices.find((x) => x.isCorrect)?.choiceText}
                                    />
                                </div>
                            </div>
                        </Col>
                    )}
                </Row>

                <Divider />

                {/* Preview section */}
                <Row>
                    <Col sm={24}>
                        <div className="designer-cell designer-control preview-box">
                            <p className="designer-preview-heading">Preview</p>
                            <p style={{ maxWidth: "560px" }}>{question?.title}</p>

                            {/* Preview of choices (conditional based on question type) */}
                            {question?.questionType && (
                                <Radio.Group name={question?.title} value={uiModel.highlightedChoice}>
                                    <Space direction={question?.questionType === "choice" ? "vertical" : "horizontal"}>
                                        {uiModel.previewChoices.map((choice) => (
                                            <Radio
                                                key={choice.choiceId}
                                                value={choice.choiceId}
                                                className={`radio-border ${
                                                    uiModel.highlightedChoice === choice.choiceId ? "selected" : ""
                                                }`}
                                            >
                                                {choice.choiceText}
                                            </Radio>
                                        ))}
                                    </Space>
                                </Radio.Group>
                            )}
                        </div>
                    </Col>
                </Row>
            </Card>
        </Collapsible>
    );
};

export default DesignerRow;
